import { Form, Layout, FormLayout, ChoiceList, TextField, Button, Popover, DatePicker, Range, Text, InlineCode } from "@shopify/polaris";
import { useField, useForm, notEmpty, SubmitResult } from "@shopify/react-form";
import { useNavigate } from "react-router-dom";
import { formSubmitHandler } from "../../../utils/forms";
import { getSettings, updateSettings } from "../../../requests";
import styles from "./SettingsV2.module.css";
import { REQUIRED_FIELD_MESSAGE } from "../../../constants/validation";
import { useCallback, useEffect, useState } from "react";
import formConfig, { defaultValues } from "../config";
import { getInitialValues, getRequestPayload, isFormValues, sortAlphabetically } from "../utils";
import { TRACKING_SCOPES } from "../constants";
import { settingsUpdatedMessageConfig } from "../../../utils/messages";
import { handleError } from "../../../utils/errorHandling";

const config = {
    request: updateSettings,
  };
  
  const codeSnippet = `<script type="text/javascript">
    var customer_id={{customer.id}};
  </script>`;

interface SettingsProps {
  showMessage: (message: any) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
}

function SettingsComponent({ showMessage, isLoading, setIsLoading }: SettingsProps) {

    const navigate = useNavigate();
    const [popoverActive, setPopoverActive] = useState(false);
    const [initialValues, setInitialValues] = useState(defaultValues);
  
    const [{ month, year }, setMonth] = useState({
      month: new Date().getMonth(),
      year: new Date().getFullYear(),
    });
  
    const [selectedDates, setSelectedDates] = useState({
      start: new Date(),
      end: new Date(),
    });

    const { fields, submit, submitting, dirty } = useForm({
        fields: {
          eventCategories: useField({
            value: initialValues.eventCategories,
            validates: [],
          }),
          webhookCategories: useField({
            value: initialValues.webhookCategories,
            validates: [],
          }),
          iterableApiKey: useField({
            value: initialValues.iterableApiKey,
            validates: [notEmpty(REQUIRED_FIELD_MESSAGE)],
          }),
          productTracking: useField({
            value: initialValues.productTracking,
            validates: [],
          }),
          storeType: useField({
            value: initialValues.storeType,
            validates: [],
          }),
        },
        async onSubmit(formValues): Promise<SubmitResult> {
          const payload = getRequestPayload(
            formValues,
            initialValues,
            selectedDates
          );
          const submitResult = await formSubmitHandler(
            navigate,
            config,
            payload,
            showMessage
          );
    
          if (submitResult?.status === "success") {
            showMessage(settingsUpdatedMessageConfig);
          }
    
          return submitResult;
        },
        makeCleanAfterSubmit: true,
      });
    
      // TODO: consider implement generic handler as we can use `name` argument
      const handleEventCategoriesChange = (
        selected: string[],
        name: string
      ): void => {
        fields?.eventCategories?.onChange(sortAlphabetically(selected));
      };
    
      const handleWebhookCategoriesChange = (
        selected: string[],
        name: string
      ): void => {
        fields?.webhookCategories?.onChange(sortAlphabetically(selected));
      };
    
      const handleIterableKeyChange = (value: string): void => {
        const field = fields?.iterableApiKey;
    
        if (!field.touched) {
          field.onChange("");
        } else {
          field.onChange(value);
        }
      };
    
      const handleProductTrackingChange = (
        selected: string[],
        name: string
      ): void => {
        fields?.productTracking?.onChange(sortAlphabetically(selected));
      };
    
      const handleMonthChange = (month: number, year: number): void => {
        setMonth({ month, year });
      };
    
      const handleDateChange = (date: Range) => {
        setSelectedDates(date);
        togglePopoverActive();
      };
    
      const togglePopoverActive = useCallback(
        () => setPopoverActive((popoverActive) => !popoverActive),
        []
      );
    
      const handleProductTrackingClick = useCallback(() => {
        if (!fields?.productTracking?.value?.includes(TRACKING_SCOPES.catalog)) {
          togglePopoverActive();
        }
      }, [togglePopoverActive, fields]);
    
      const activator = (
        <div role="button" onClick={handleProductTrackingClick}>
          <ChoiceList
            name="productTracking"
            allowMultiple
            title="Product Tracking"
            disabled={isLoading}
            choices={formConfig.productTrackingChoices}
            selected={fields.productTracking.value}
            onChange={handleProductTrackingChange}
          />
        </div>
      );

      useEffect(() => {
        const fetchData = async () => {
          try {
            setIsLoading(true);
            const { data } = await getSettings();
            
            if (isFormValues(data)) {
              setInitialValues(getInitialValues(data));
            }
            
          } catch (err) {
            handleError(err, showMessage, navigate);
          } finally {
            setIsLoading(false);
          }
        };
    
        fetchData();
      }, []); // eslint-disable-line

    return (
        <>
        <Form noValidate preventDefault onSubmit={submit}>
            <Layout>
              <Layout.Section variant="oneHalf">
                <FormLayout>
                  <ChoiceList
                    name="eventCategories"
                    allowMultiple
                    title="Behavioral Event Tracking"
                    disabled={isLoading}
                    choices={formConfig.clientSideEventChoices}
                    selected={fields.eventCategories.value}
                    onChange={handleEventCategoriesChange}
                  />
                </FormLayout>
                <div className={styles.snippetContainer}>
                  <p className={styles.instructions}>
                    In order to add more metadata to the events coming to
                    Iterable, please add the following snippet to your store
                    theme code which generally lies in "theme.liquid".
                  </p>
                  <pre className={styles.preformatted}>
                    <Text as="span">
                      <InlineCode>{codeSnippet}</InlineCode>
                    </Text>
                  </pre>
                </div>
              </Layout.Section>
              <Layout.Section variant="oneHalf">
                <FormLayout>
                  <ChoiceList
                    name="webhookCategories"
                    allowMultiple
                    title="Commerce Event Tracking"
                    disabled={isLoading}
                    choices={formConfig.weebhookEventChoices}
                    selected={fields.webhookCategories.value}
                    onChange={handleWebhookCategoriesChange}
                  />

                  <TextField
                    name="iterableApiKey"
                    label="Iterable API Key"
                    autoComplete="off"
                    disabled={isLoading}
                    {...fields.iterableApiKey}
                    onChange={handleIterableKeyChange}
                  />

                  <Popover
                    active={popoverActive}
                    activator={activator}
                    fluidContent
                    onClose={togglePopoverActive}
                  >
                    <div className={styles.datepickerWrapper}>
                      <DatePicker
                        month={month}
                        year={year}
                        onChange={handleDateChange}
                        onMonthChange={handleMonthChange}
                        selected={selectedDates}
                        disableDatesAfter={new Date()}
                      />
                    </div>
                  </Popover>
                </FormLayout>
              </Layout.Section>
              <Layout.Section>
                <div className={styles.buttonsContainer}>
                  <Button
                    variant="primary"
                    submit
                    loading={submitting}
                    disabled={isLoading || !dirty}
                  >
                    Update
                  </Button>
                </div>
              </Layout.Section>
            </Layout>
          </Form>
        </>
        );
}

export default SettingsComponent;